import { type FC, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { selectProductData } from "~/domains/product/redux/selectors";
import { type SrpProductData } from "~/utils/models";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { AddToCartAction } from "~/features/search/components/ProductResults/views/TabularView/ProductTabularViewActionCell/AddToCartAction";
import { AddToWishlistAction } from "~/features/search/components/ProductResults/views/TabularView/ProductTabularViewActionCell/AddToWishlistAction";
import { OpenCadAction } from "~/features/search/components/ProductResults/views/TabularView/ProductTabularViewActionCell/OpenCadAction";
import { OpenCalcAction } from "~/features/search/components/ProductResults/views/TabularView/ProductTabularViewActionCell/OpenCalcAction";
import { styled } from "~/common/mui";
import { C_MEDIAS_PLUS, C_PRIMARY_HOVER } from "~/common/components/icon/Colors";

interface Props {
  productCode: string;
  isHousing?: boolean;
}

export const ProductTabularViewActionCell: FC<Props> = ({ productCode, isHousing = false }: Props) => {
  const product = useSelector(selectProductData(productCode)) as SrpProductData;

  const showWishlistBtn = canSeeWishlistButton();
  const showATCBtn = canSeeAddToCartButton();

  return (
    <ProductTabularViewActionCellLayout>
      <OpenCalcAction
        url={product.calcUrl}
        data-ta-spec={isHousing ? "housing_assistant_product_calc" : undefined}
        data-test-id={"tabular-view-action-button-calc"}
      />
      <OpenCadAction
        url={product.cadUrl}
        hasCad={product.hasCad}
        target={"_blank"}
        data-test-id={"tabular-view-action-button-cad"}
        data-ta-spec={isHousing ? "housing_assistant_product_cad" : undefined}
        data-ta-title={isHousing ? product.designation : undefined}
      />

      {showWishlistBtn && <AddToWishlistAction productCode={productCode} />}

      {showATCBtn && <AddToCartAction productCode={productCode} />}
      {!showATCBtn && <div />}
    </ProductTabularViewActionCellLayout>
  );
};

export const ProductTabularViewActionCellLayout: FC<{ children: ReactNode }> = ({ children }) => (
  <ActionCellRoot>
    <ActionCellInner>{children}</ActionCellInner>
  </ActionCellRoot>
);

export const ActionTextLinkCell: FC<{
  children?: ReactNode;
  href?: string;
  target?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  cad?: boolean;
  invisible?: boolean;
  missingCad?: boolean;
}> = ({ onClick, disabled, className, cad, invisible, missingCad, ...rest }) => {
  return (
    <ActionTextLink
      {...rest}
      $cad={cad}
      $invisible={disabled || invisible}
      $missingCad={missingCad}
      aria-disabled={disabled ? true : undefined}
      className={className}
      onClick={disabled ? undefined : onClick}
      tabIndex={disabled ? -1 : undefined}
    />
  );
};

const ActionCellRoot = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
});

const ActionCellInner = styled("div")(({ theme }) => ({
  display: "flex",
  marginLeft: "auto",
  background: theme.palette.primary.main,
  "& > *": {
    height: theme.spacing(4.75),
    width: theme.spacing(4.75),
  },
}));

const ActionTextLink = styled("a", {
  shouldForwardProp: (prop) => prop !== "$cad" && prop !== "$invisible" && prop !== "$missingCad",
})<{
  $cad?: boolean;
  $invisible?: boolean;
  $missingCad?: boolean;
}>(({ theme, $cad, $invisible, $missingCad }) => ({
  color: theme.palette.primary.contrastText,
  padding: $cad ? 0 : `0 ${theme.spacing(1.25)}`,
  background: theme.palette.primary.main,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  visibility: $invisible ? "hidden" : undefined,
  ...($missingCad && {
    background: C_MEDIAS_PLUS,
    width: "100%",
    height: "100%",
  }),
  "&:hover": {
    background: C_PRIMARY_HOVER,
    color: theme.palette.primary.contrastText,
  },
  "&:visited, &:active, &:focus": {
    color: theme.palette.primary.contrastText,
  },
  ...($cad && {
    "& > img": {
      width: theme.spacing(3.125),
    },
  }),
}));
